import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsArticle } from "@/types/news";
import { formatViews, getCategoryName, relativeTime } from "@/utils/news";

export interface Slide {
  image: string;
  article: NewsArticle;
}

export default function FeatureCarousel({ slides }: { slides: Slide[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const current = slides[index];

  const go = (next: number) => setIndex((next + slides.length) % slides.length);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[400px] w-full md:h-[540px]">
        {slides.map((slide, i) => (
          <div
            key={slide.article.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={slide.article.title}
              title={`${slide.article.title} - 51爆料网`}
              className={`h-full w-full object-cover object-top ${i === index ? "animate-kenburns" : ""}`}
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/30" />

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full max-w-[1280px] flex-col justify-between px-4 py-6 md:px-6 md:py-8">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 font-label text-sm font-extrabold text-background-50">
                51
              </span>
              <h1 className="font-heading text-sm font-semibold text-background-50 md:text-base">
                51爆料网
                <span className="ml-2 font-normal text-background-100/80">
                  权威实时爆料新闻与热点资讯平台
                </span>
              </h1>
            </div>

            <div className="max-w-3xl">
              <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-background-100/90">
                <span className="rounded-full bg-accent-500 px-3 py-1 font-medium text-background-50">
                  焦点爆料
                </span>
                <span className="rounded-full bg-background-50/15 px-3 py-1">
                  {getCategoryName(current.article.category)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {relativeTime(current.article.publishTime)}
                </span>
                <span className="hidden items-center gap-1 sm:flex">
                  <i className="ri-eye-line" />
                  {formatViews(current.article.views)} 阅读
                </span>
              </div>

              <h2 className="font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">
                <Link to={`/detail/${current.article.id}`} className="transition-colors hover:text-accent-300">
                  {current.article.title}
                </Link>
              </h2>

              <p className="news-clamp-2 mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-100/85 md:block">
                {current.article.summary}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${current.article.id}`}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-article-line" />
                  阅读全文
                </Link>
                <Link
                  to={`/category/${current.article.category}`}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/20"
                >
                  更多{getCategoryName(current.article.category)}爆料
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:left-6 md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:right-6 md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        <div className="absolute bottom-5 right-4 flex items-center gap-2 md:right-6">
          {slides.map((slide, i) => (
            <button
              key={slide.article.id}
              type="button"
              aria-label={`第 ${i + 1} 张`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/60"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}